/**
 * Application Designer — the design session a native page opens.
 *
 * One hook holds everything a design sitting needs: the saved document, the
 * draft being edited, the undo/redo history, the current selection, and the
 * load/save round trip. A native page supplies its descriptor and its two API
 * functions and gets back the whole editor state, so adding a designable page
 * does not mean reimplementing draft handling.
 *
 * The draft is the session. `designing` is simply "a draft exists": starting
 * design clones the saved document, Cancel throws the clone away, and Save
 * replaces the saved document with what the server echoed back. The native page
 * stays mounted throughout and keeps its own data — which is what lets the page
 * itself remain the renderer instead of the designer previewing a copy of it.
 *
 * Nothing here touches clinical data, and nothing here decides what an element
 * may show. Those stay with the host.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LAYOUT_MODE_GRID } from "./layoutModes";
import { canDesignPages } from "./designPermissions";
import {
  defaultLayoutDocument,
  defaultLayoutItems,
  layoutDocumentBounds,
  layoutDocumentPayload,
  normalizeLayoutDocument,
  normalizeLayoutItems,
  styleLayoutItem,
} from "./layoutDocument";
import {
  hideFlowItem,
  moveFlowItem,
  placeFlowItem,
  showFlowItem,
  visibleItemsInColumn,
  hiddenItems as hiddenFlowItems,
} from "./flowLayout";
import {
  hideGridItem,
  moveGridItem,
  resizeGridItem,
  showGridItem,
  visibleGridItems,
} from "./gridLayout";
import {
  canRedo,
  canUndo,
  createHistory,
  pushHistory,
  redoHistory,
  resetHistory,
  undoHistory,
} from "./history";
import { descriptorCosmetics, descriptorElementTitle, isDescriptorElement } from "./pageDescriptor";

const DEFAULT_MESSAGES = Object.freeze({
  saved: "Layout saved for the organization.",
  saveFailed: "Failed to save the layout.",
});

export function useApplicationDesigner({
  descriptor,
  loadLayout,
  saveLayout,
  user,
  messages = DEFAULT_MESSAGES,
}) {
  const canDesign = canDesignPages(user);
  const [saved, setSaved] = useState(() => defaultLayoutDocument(descriptor));
  const [history, setHistory] = useState(null);
  const [selection, setSelection] = useState(null);
  const [busy, setBusy] = useState(false);

  const designing = history !== null;
  const items = designing ? history.present : saved.items;
  const bounds = useMemo(() => layoutDocumentBounds(descriptor), [descriptor]);
  const known = useCallback((id) => isDescriptorElement(descriptor, id), [descriptor]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(loadLayout)
      .then((payload) => {
        if (!cancelled) setSaved(normalizeLayoutDocument(descriptor, payload));
      })
      .catch(() => {
        if (!cancelled) setSaved(defaultLayoutDocument(descriptor));
      });
    return () => {
      cancelled = true;
    };
    // The descriptor is a frozen module-level value; the loader is the page's.
  }, [descriptor, loadLayout]);

  /** Every edit goes through here, so history and refusal are handled once. */
  const commit = useCallback((next) => {
    setHistory((current) => (current ? pushHistory(current, next) : current));
  }, []);

  const start = useCallback(() => {
    setHistory(createHistory(normalizeLayoutItems(descriptor, saved.items)));
    setSelection(null);
  }, [descriptor, saved.items]);

  const cancel = useCallback(() => {
    setHistory(null);
    setSelection(null);
  }, []);

  const restoreDefault = useCallback(() => {
    setHistory(resetHistory(normalizeLayoutItems(descriptor, defaultLayoutItems(descriptor))));
    setSelection(null);
  }, [descriptor]);

  const undo = useCallback(() => setHistory((current) => undoHistory(current)), []);
  const redo = useCallback(() => setHistory((current) => redoHistory(current)), []);

  const place = useCallback(
    (id, column, beforeId = null) => {
      if (descriptor.mode === LAYOUT_MODE_GRID) return;
      commit(placeFlowItem(items, id, column, beforeId, { columnCount: descriptor.columns, known }));
    },
    [commit, descriptor, items, known],
  );

  const move = useCallback(
    (id, direction) => {
      if (descriptor.mode === LAYOUT_MODE_GRID) return;
      commit(moveFlowItem(items, id, direction, { columnCount: descriptor.columns, known }));
    },
    [commit, descriptor, items, known],
  );

  const moveTo = useCallback(
    (id, col, row) => {
      if (descriptor.mode !== LAYOUT_MODE_GRID) return;
      commit(moveGridItem(items, id, col, row, bounds));
    },
    [bounds, commit, descriptor.mode, items],
  );

  const resize = useCallback(
    (id, width, height) => {
      if (descriptor.mode !== LAYOUT_MODE_GRID) return;
      commit(resizeGridItem(items, id, width, height, bounds));
    },
    [bounds, commit, descriptor.mode, items],
  );

  const hide = useCallback(
    (id) => {
      commit(descriptor.mode === LAYOUT_MODE_GRID ? hideGridItem(items, id) : hideFlowItem(items, id));
      setSelection((current) => (current === id ? null : current));
    },
    [commit, descriptor.mode, items],
  );

  const show = useCallback(
    (id) => {
      commit(
        descriptor.mode === LAYOUT_MODE_GRID
          ? showGridItem(items, id, bounds)
          : showFlowItem(items, id),
      );
    },
    [bounds, commit, descriptor.mode, items],
  );

  const setStyle = useCallback(
    (id, style) => commit(styleLayoutItem(items, id, style, descriptorCosmetics(descriptor, id))),
    [commit, descriptor, items],
  );

  const persist = useCallback(async () => {
    setBusy(true);
    try {
      const echoed = await saveLayout(layoutDocumentPayload(descriptor, items));
      setSaved(normalizeLayoutDocument(descriptor, { ...echoed, source: "organization" }));
      setHistory(null);
      setSelection(null);
      toast.success(messages.saved || DEFAULT_MESSAGES.saved);
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || messages.saveFailed || DEFAULT_MESSAGES.saveFailed,
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [descriptor, items, messages.saveFailed, messages.saved, saveLayout]);

  const hidden = useMemo(
    () => (designing ? hiddenFlowItems(items, (id) => descriptorElementTitle(descriptor, id)) : []),
    [descriptor, designing, items],
  );

  const inColumn = useCallback((column) => visibleItemsInColumn(items, column), [items]);
  const visible = useMemo(() => visibleGridItems(items), [items]);

  return {
    descriptor,
    canDesign,
    designing,
    busy,
    items,
    saved,
    bounds,
    hidden,
    selection,
    select: setSelection,
    inColumn,
    visible,
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    start,
    cancel,
    restoreDefault,
    undo,
    redo,
    place,
    move,
    moveTo,
    resize,
    hide,
    show,
    setStyle,
    save: persist,
  };
}
